import { useState, useEffect } from 'react';
import {
  Bar, Line, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { api } from '../api';
import {
  PageHeader, Card, Stat, Pill, Field, Select,
  Table, THead, TH, TR, TD, Empty, SourceNote, InfoDot, DataTable,
} from '../components/ui';

const H = (v) => `${Number(v || 0).toFixed(0)}h`;
const PCT = (v) => `${Number(v || 0).toFixed(1)}%`;

const SOURCES = [
  { source: 'Magnetic', label: 'Schedule capacity', detail: '/scheduling/capacity baseDayCapacity — scheduled working hours per person/day' },
  { source: 'Magnetic', label: 'Allocated (booked)', detail: '/scheduling/capacity estimatedTime — hours booked onto tasks (planned allocation)' },
  { source: 'Magnetic', label: 'Available / bench', detail: '/scheduling/capacity remainingCapacityMinutes — capacity − allocated; schedule headroom' },
  { source: 'Magnetic', label: 'Unbilled', detail: 'time entries on non-billable projects (tracking-side, not scheduling)' },
  { source: 'BambooHR', label: 'Department', detail: 'used to roll up team-level totals' },
  { source: 'Derived', label: 'Utilisation', detail: 'allocated ÷ capacity. Bench = under 80%. Over-allocated = allocated > capacity.' },
];

const utilTone = (u) => (u >= 100 ? 'danger' : u >= 80 ? 'ok' : u >= 50 ? 'warn' : 'danger');

export default function Utilisation() {
  const [period, setPeriod] = useState('');
  const [periods, setPeriods] = useState([]);
  const [level, setLevel] = useState('person');     // 'team' | 'person'
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [tab, setTab] = useState('utilisation');

  useEffect(() => {
    api.reporting.periods().then((d) => { setPeriods(d); setPeriod(d[0] || ''); }).catch(() => {});
    api.reporting.trend(6).then(setTrend).catch(() => {});
  }, []);

  useEffect(() => {
    if (!period) return;
    api.reporting.utilisation(period).then(setData).catch(() => {});
  }, [period]);

  const s = data?.summary;
  const rows = data ? (level === 'team' ? data.teams : data.people) : [];
  const benchRows = data ? data.people.filter((p) => p.bench) : [];
  const nameCol = level === 'team' ? 'department' : 'full_name';

  return (
    <div>
      <PageHeader
        eyebrow="Reporting"
        title="Allocation & utilisation"
        subtitle="Who is booked and who has bench — straight from Magnetic's resource schedule. Utilisation = scheduled allocation ÷ capacity (not logged hours). Teams are grouped by Magnetic 'grouping' (Engineering / QA / PM / Design / …). Non-Makers (POs, leadership, finance) are listed too, marked 'not scheduled'."
        actions={
          <div className="flex items-end gap-3">
            <Field label="Level">
              <Select value={level} onChange={(e) => setLevel(e.target.value)}>
                <option value="person">Person</option>
                <option value="team">Team</option>
              </Select>
            </Field>
            <Field label="Month">
              <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
                {periods.map((p) => <option key={p} value={p}>{p}</option>)}
              </Select>
            </Field>
          </div>
        }
      />

      <SourceNote items={SOURCES} />

      {s && (
        <div className="grid grid-cols-2 xl:grid-cols-6 gap-4 mb-8">
          <Stat label="Headcount" value={s.headcount} info="People with a Magnetic schedule this period." />
          <Stat label="Capacity" value={H(s.capacity)} sub={`allocated ${H(s.allocated)}`}
            info="Σ scheduled working hours (Magnetic baseDayCapacity). Allocated = Σ booked onto tasks." />
          <Stat label="Utilisation" value={PCT(s.utilisation)} sub={`bench ${s.bench}`}
            info="Allocated ÷ capacity. Bench = people under 80%." />
          <Stat label="Available" value={H(s.available)} sub="bench headroom"
            info="Σ (capacity − allocated) from Magnetic remainingCapacityMinutes — schedule headroom across everyone." />
          <Stat label="Over-allocated" value={s.over_allocated} sub="people booked past capacity"
            info="People whose allocated hours exceed their scheduled capacity (likely overtime upcoming)." />
          <Stat label="Unbilled" value={H(s.unbilled)} sub="non-billable logged"
            info="Logged hours on non-billable Magnetic projects (tracking-side)." />
        </div>
      )}

      <nav className="flex gap-6 border-b border-slate-700 mb-6 flex-wrap">
        {[['utilisation', 'Utilisation'], ['unbilled', 'Unbilled'], ['bench', `Bench (${benchRows.length})`], ['trend', 'Monthly comparison']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`py-3 text-sm font-medium border-b-2 -mb-px ${tab === id ? 'border-brand-500 text-slate-100' : 'border-transparent text-slate-500 hover:text-slate-200'}`}>
            {label}
          </button>
        ))}
      </nav>

      {tab === 'utilisation' && (
        <Card padding="p-0">
          {rows.length === 0 ? <Empty>No data — run pipeline/sync_scheduling.py to pull the Magnetic schedule.</Empty> : (
            <DataTable
              initialSort={{ key: 'utilisation', dir: 'desc' }}
              empty="No matches."
              columns={level === 'team' ? [
                { key: 'department', label: 'Team' },
                { key: 'headcount', label: 'People', align: 'right', mono: true, muted: true, filter: false },
                { key: 'capacity', label: 'Capacity', align: 'right', mono: true, muted: true, filter: false, render: (r) => H(r.capacity), info: 'Σ scheduled working hours (Magnetic baseDayCapacity, leave-adjusted).' },
                { key: 'allocated', label: 'Allocated', align: 'right', mono: true, filter: false, render: (r) => H(r.allocated), info: 'Σ booked onto tasks (Magnetic estimatedTime).' },
                { key: 'available', label: 'Available', align: 'right', mono: true, filter: false, render: (r) => r.available > 0 ? H(r.available) : '—', info: 'Capacity − allocated. Bench headroom.' },
                { key: 'utilisation', label: 'Utilisation', align: 'right', filter: false, render: (r) => <Pill tone={utilTone(r.utilisation)}>{PCT(r.utilisation)}</Pill> },
                { key: 'bench_count', label: 'On bench', align: 'right', mono: true, filter: false },
              ] : [
                { key: 'full_name', label: 'Person', render: (r) => (
                  <div>
                    <div className="text-slate-200">{r.full_name}</div>
                    <div className="text-[11px] text-slate-500">{r.grouping}{r.title ? ` · ${r.title}` : ''}</div>
                  </div>) },
                { key: 'grouping', label: 'Team', filter: 'select' },
                { key: 'capacity', label: 'Capacity', align: 'right', mono: true, muted: true, filter: false, render: (r) => H(r.capacity) },
                { key: 'allocated', label: 'Allocated', align: 'right', mono: true, filter: false, render: (r) => H(r.allocated) },
                { key: 'available', label: 'Available', align: 'right', mono: true, filter: false, render: (r) => r.available > 0 ? H(r.available) : '—' },
                { key: 'utilisation', label: 'Util', align: 'right', filter: false, render: (r) => <Pill tone={utilTone(r.utilisation)}>{PCT(r.utilisation)}</Pill> },
                { key: 'status', label: 'Status', filter: 'select', accessor: (r) => !r.scheduled ? 'Not scheduled' : r.over_allocated ? 'Over-allocated' : r.bench ? 'Bench' : 'Booked',
                  render: (r) => !r.scheduled ? <Pill tone="neutral">Not scheduled</Pill>
                    : r.over_allocated ? <Pill tone="danger">Over-allocated</Pill>
                    : r.bench ? <Pill tone="warn">Bench</Pill>
                    : <Pill tone="ok">Booked</Pill> },
              ]}
              rows={rows}
            />
          )}
        </Card>
      )}

      {tab === 'unbilled' && (
        <Card padding="p-0">
          {rows.length === 0 ? <Empty>No data.</Empty> : (
            <Table>
              <THead>
                <TH>{level === 'team' ? 'Team' : 'Person'}</TH>
                <TH align="right"><span className="inline-flex items-center gap-1 justify-end">Tracked <InfoDot>Logged time (Magnetic schedule view).</InfoDot></span></TH>
                <TH align="right"><span className="inline-flex items-center gap-1 justify-end">Unbilled <InfoDot>Logged hours on non-billable Magnetic projects (internal, admin, pre-sales).</InfoDot></span></TH>
                <TH align="right"><span className="inline-flex items-center gap-1 justify-end">% unbilled <InfoDot>Unbilled ÷ tracked.</InfoDot></span></TH>
              </THead>
              <tbody>
                {rows.map((r, i) => {
                  const pct = r.tracked > 0 ? (100 * r.unbilled / r.tracked) : 0;
                  return (
                    <TR key={i}>
                      <TD>{r[nameCol]}{level === 'person' && <span className="text-slate-500"> · {r.grouping}{r.title ? ` · ${r.title}` : ''}</span>}</TD>
                      <TD align="right" mono>{H(r.tracked)}</TD>
                      <TD align="right" mono className={r.unbilled > 0 ? 'text-amber-600' : ''}>{r.unbilled > 0 ? H(r.unbilled) : '—'}</TD>
                      <TD align="right"><Pill tone={pct > 20 ? 'danger' : pct > 10 ? 'warn' : 'ok'}>{PCT(pct)}</Pill></TD>
                    </TR>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {tab === 'bench' && (
        <div className="space-y-6">
          <Card padding="p-0" title="On the bench" subtitle={`Utilisation under ${data?.bench_threshold || 80}% — available capacity against the Magnetic schedule (capacity − allocated).`}>
            {benchRows.length === 0 ? <Empty>Nobody on the bench this period.</Empty> : (
              <Table>
                <THead>
                  <TH>Person</TH><TH>Team</TH><TH align="right">Capacity</TH>
                  <TH align="right">Allocated</TH><TH align="right">Available</TH><TH align="right">Utilisation</TH>
                </THead>
                <tbody>
                  {benchRows.map((p, i) => (
                    <TR key={i}>
                      <TD>{p.full_name}</TD>
                      <TD muted>{p.department}</TD>
                      <TD align="right" mono muted>{H(p.capacity)}</TD>
                      <TD align="right" mono>{H(p.allocated)}</TD>
                      <TD align="right" mono className="text-amber-600">{H(p.available)}</TD>
                      <TD align="right"><Pill tone={utilTone(p.utilisation)}>{PCT(p.utilisation)}</Pill></TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          {data?.people?.some((p) => p.over_allocated) && (
            <Card padding="p-0" title="Over-allocated" subtitle="People booked past their scheduled capacity — likely overtime coming.">
              <Table>
                <THead><TH>Person</TH><TH>Team</TH><TH align="right">Capacity</TH><TH align="right">Allocated</TH><TH align="right">Over by</TH></THead>
                <tbody>
                  {data.people.filter((p) => p.over_allocated).map((p, i) => (
                    <TR key={i}>
                      <TD>{p.full_name}</TD>
                      <TD muted>{p.department}</TD>
                      <TD align="right" mono muted>{H(p.capacity)}</TD>
                      <TD align="right" mono>{H(p.allocated)}</TD>
                      <TD align="right" mono className="text-brand-500">+{H(p.allocated - p.capacity)}</TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}
        </div>
      )}

      {tab === 'trend' && (
        <Card title="Monthly comparison" subtitle="Utilisation % (bars) and available bench hours (line) over recent months.">
          {trend.length === 0 ? <Empty>No history.</Empty> : (
            <div style={{ width: '100%', height: 360 }}>
              <ResponsiveContainer>
                <ComposedChart data={trend} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="period" stroke="#94a3b8" fontSize={12} />
                  <YAxis yAxisId="l" stroke="#94a3b8" fontSize={12} unit="%" />
                  <YAxis yAxisId="r" orientation="right" stroke="#94a3b8" fontSize={12} unit="h" />
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="l" dataKey="utilisation" name="Utilisation %" fill="#e92030" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="r" dataKey="available" name="Available h" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="r" dataKey="unbilled" name="Unbilled h" stroke="#64748b" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
